import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { ProfileReportsService } from '../../services/profile-reports.service';
import { Report } from '../../models/report';
import { checkDuplicateName } from '../../validation/report-profile-validation';
import { ReportProfileBase } from '../base-report/firm-managment-report.base';
import { forkJoin, Observable } from 'rxjs';

@Component({
	selector: 'app-firm-management-report-profile',
	templateUrl: './firm-management-report-profile.component.html',
	styleUrls: ['./firm-management-report-profile.component.css'],
})
export class FirmManagementReportProfileComponent extends ReportProfileBase implements OnInit {

	@Input() profileId: string;
	// eslint-disable-next-line @angular-eslint/no-output-native
	@Output() close = new EventEmitter();
	profileTypes: string[];
	reports: Report[] = [];


	editProfileData: any;
	header = 'Add Profile';
	isBothStaffAndFirmProfileChange: boolean;

	get isCustomNameValid(): boolean {
		return this.isReportGroupValid('name');
	}
	get isReportValid(): boolean {
		return this.isReportGroupValid('id');
	}

	validations = {
		required: '{0} is required',
		name: {
			ReportProfileNameMustBeUnique: '{0} already exists',
		},
		reportProfileSelectionName: {
			customNameMustBeUnique: '{0} already exists'
		}
	};

	constructor(public fb: FormBuilder, public profileReportsService: ProfileReportsService) {
		super(fb, profileReportsService);
		this.formControlInit();

	}

	async ngOnInit() {
		const bindings = await this.getBindings();
		this.companyId = bindings.ApplicationConfiguration.CompanyId;
		this.contactId = bindings.ApplicationConfiguration.ContactId;

		this.loadDefaultFormData();
	}

	formControlInit() {
		// set reactive form control
		this.profileForm = this.fb.group({
			id: [null],
			name: [null, [Validators.required]],
			reportDateRangeId: [null],
			profileType: [null, [Validators.required]],
			reportProfileSelections: this.fb.array([])
		});
	}

	loadDefaultFormData() {
		// load default data required for form
		this.readEmployeeId();
		this.loadReportDefinition();
		this.profileReportsService.profileType().subscribe(info => {
			// set default value for dropdown
			this.profileTypes = info;
			this.profileForm.get('profileType').setValue(info[0]);
		});

		if (this.profileId) {
			// load form data
			this.header = 'Edit Profile';
			this.readProfileDetail();
		} else {
			this.addReport();
		}
	}
	/** Used to call reports tree's api and make recursive call if it has more than 1 pages */
	loadReportDefinition() {

		if (this.profileReportsService.reports.length > 0) {
			this.reports = this.profileReportsService.reports;
		} else {
			this.profileReportsService.reportDefinition(this.companyId, 1).subscribe(firstResult => {

				let itemIndex = 1;
				const requests: Observable<any>[] = [];

				// let pageNumber = 2 : we set 2 because for 1st time it getting from main method.
				for (let pageNumber = 2; pageNumber <= firstResult.totalPages; pageNumber++) {
					// to find next index for next request.
					itemIndex = itemIndex + firstResult.itemsPerPage;
					requests.push(this.profileReportsService.reportDefinition(this.companyId, itemIndex));
				}

				if (requests.length === 0) {
					// this trigger only when if we don't have next page for reading. so it will set firstreult to tree
					this.reports = firstResult.items;
					this.profileReportsService.reports = this.reports;
				}

				forkJoin(requests).subscribe(allResults => {
					let data = [];
					allResults.push(firstResult);

					allResults.forEach(element => {
						data = data.concat(element.items);
					});
					this.reports = data;
					this.profileReportsService.reports = this.reports;
				});
			});
		}
	}

	mapReportSelections(selectionCount) {
		for (let i = 0; i < selectionCount; i++) {
			this.addReport();
		}
	}
	mapProfileType() {
		if (this.profileDetail && this.profileDetail.employeeId) {
			this.profileForm.get('profileType').setValue('Staff');
		} else {
			this.profileForm.get('profileType').setValue('Firm');
		}
	}

	createReportArray(): FormGroup {
		return this.fb.group({
			reportDefinition: this.fb.group({
				id: ['', [Validators.required]],
				code: [null],
				name: [null]
			}),
			id: [null],
			reportOptionsData: [null],
			name: [null, [Validators.required]]
		});
	}

	addReport(): void {
		this.reportsFA.push(this.createReportArray());
	}

	deleteReport(i: number): void {
		this.reportsFA.removeAt(i);
	}

	profileSubmit(): void {
		this.alerts = [];
		if (!this.isSubmitAllowed) {
			this.showAlerts();
			this.markFormGroupTouched(this.profileForm);
			return;
		}

		// valid form
		if (this.profileId) {
			this.editProfile();
		} else {
			this.isLoaderBusy = true;
			this.saveProfile();
		}
	}

	editProfile() {
		this.isLoaderBusy = true;
		this.editCurrentProfile();
	}

	editCurrentProfile() {
		const model = this.updateDto();
		this.profileReportsService.updateProfile(model).subscribe(
			() => this.saveProfileSuccess(),
			(error) => this.saveProfileError(error),
			() => this.isLoaderBusy = false
		);
	}

	onChange(index: number, id: string): void {
		const fg = this.reportsFA.at(index);
		const fgDefinition = fg.get('reportDefinition');

		const info = this.reports.find(x => x.id === id);
		fg.get('id').setValue(null);
		fg.get('name').setValue(info.name);
		fgDefinition.get('id').setValue(id);
		fgDefinition.get('code').setValue(info.systemId);
		fgDefinition.get('name').setValue(info.name);
		this.checkCustomNameExist(index);
	}

	saveProfile(): void {
		const model = this.convertFormToModel();
		this.profileReportsService.saveProfile(model).subscribe(
			() => this.saveProfileSuccess(),
			(error) => this.saveProfileError(error),
			() => this.isLoaderBusy = false
		);
	}

	updateDto() {
		if (this.profileDetail.employeeId !== this.employeeIdByProfileType) {
			// here user change form firm to staff and viceversa
			this.isBothStaffAndFirmProfileChange = true;
		}
		this.profileDetail.employeeId = this.employeeIdByProfileType;
		return Object.assign(this.profileDetail, this.profileForm.value);
	}

	convertFormToModel(): any {
		const FormValue = this.profileForm.value;
		const basic = {
			companyId: this.companyId,
			employeeId: this.employeeIdByProfileType,
		};
		return Object.assign(FormValue, basic);
	}

	saveProfileSuccess(): void {
		if (this.isBothStaffAndFirmProfileChange) {
			this.success.emit({ isStaffChange: true, isFirmChange: true });
		} else {
			this.refreshSingleProfileTree();
		}
		this.cancel();
	}

	cancel() {
		this.close.emit();
	}

	isReportGroupValid(controlName): boolean {
		return this.reportsFA.controls.some(x => {
			const control = x.get(controlName);
			return control.invalid && control.touched;
		});
	}

	setValueBeforePatch() {
		const selectionCount = this.profileDetail.reportProfileSelections.length;
		this.mapReportSelections(selectionCount);
		this.mapProfileType();
	}

	setValueAfterPatch() {
	}

	checkCustomNameExist(index) {
		const control = this.reportsFA.at(index).get('name') as AbstractControl;
		if (this.reportsFA.value.filter(x => x.name === control.value).length > 1) {
			control.setValidators([checkDuplicateName]);
		} else {
			control.clearValidators();
			control.setValidators([Validators.required]);
		}
		control.updateValueAndValidity();
	}

	firmTypeChange() {
		this.validateUniqueness();
	}
}
